import React, { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { DownloadIcon } from "lucide-react";
import { getExtensionType } from "@/utils/helper/fileUploadValidation";
import LazyImage from "../ui/LazyImage";
import { toastErrorMessage, toastSuccessMessage } from "@/utils/helper/myToast";

interface FilePreviewModalProps {
  onClose: (props?: any) => void;
  file: {
    url: string;
    name: string;
  };
}

const FilePreviewModal: React.FC<FilePreviewModalProps> = ({ onClose, file }) => {
  const [pdfError, setPdfError] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(true);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const handleDownload = async () => {
    try {
      const response = await fetch(file.url);

      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toastSuccessMessage("File downloaded successfully!");
    } catch (error) {
      console.error("Download failed:", error);
      toastErrorMessage("Failed to download file. Please try again.");
    }
  };

  const renderPreview = () => {
    const fileType = getExtensionType(file.url);
    switch (fileType) {
      case "image":
        return <LazyImage src={file.url} alt={file.name} className="max-h-[80vh] max-w-full object-contain" />;
      case "video":
        return (
          <video controls className="max-h-[80vh] max-w-full">
            <source src={file.url} />
            Your browser does not support the video tag.
          </video>
        );
      case "pdf":
        return (
          <>
            {pdfLoading && <div className="flex items-center justify-center h-[80vh] text-gray-500">Loading PDF...</div>}
            {!pdfError && (
              <iframe
                ref={iframeRef}
                src={`${file.url}#view=fit`}
                className="w-full h-[80vh]"
                title={file.name}
                hidden={pdfLoading}
                onLoad={() => {
                  setTimeout(() => {
                    try {
                      const iframeDoc = iframeRef.current?.contentDocument;
                      if (!iframeDoc || iframeDoc.body.innerHTML.trim() === "") {
                        setPdfError(true);
                      }
                    } catch (e) {
                      setPdfError(true);
                    } finally {
                      setPdfLoading(false);
                    }
                  }, 500);
                }}
              />
            )}
            {pdfError && !pdfLoading && (
              <div className="flex flex-col items-center justify-center h-full text-center space-y-4">
                <p className="text-gray-600">Preview not available</p>
                <button
                  onClick={() => window.open(file.url, "_blank")}
                  className="px-4 py-2 bg-primary text-white rounded bg-text-primary hover:bg-text-primary/80 transition"
                >
                  Open PDF in New Tab
                </button>
              </div>
            )}
          </>
        );
      default:
        return (
          <div className="flex flex-col items-center justify-center p-8">
            <div className="text-6xl mb-4">📄</div>
            <p className="text-gray-600">Preview not available</p>
          </div>
        );
    }
  };
  // console.log({ file, pdfError });
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="bg-white rounded-lg shadow-xl overflow-hidden w-fit min-h-[400px] max-w-[90vw] max-h-[90vh] relative flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <div className="flex items-center space-x-3">
              <h3 className="text-lg font-semibold text-gray-800 truncate">{file.name}</h3>
            </div>
            <div className="flex items-center space-x-2">
              <button onClick={handleDownload} className="p-2 hover:bg-gray-100 rounded-full transition-colors" title="Download">
                <DownloadIcon className="w-5 h-5 text-gray-600" />
              </button>
              <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors" title="Close">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          {/* Preview Content */}
          <div className="flex items-center justify-center h-full p-4 flex-1">{renderPreview()}</div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default FilePreviewModal;
